import { HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SortEnum } from 'src/app/utils/enums/sort-enum';
import { BcbConditionnement } from 'src/app/utils/interfaces/new/bcb-conditionnement.interface';
import { BcbPosologieStructuree, BcbControlePosologieResult, BcbPosologieStructuree2, BcbControlePosologieResult2 } from 'src/app/utils/interfaces/new/bcb-poslogie-structuree.interface';
import { BcbPrescription } from 'src/app/utils/interfaces/new/bcb-prescription.interface';
import { BCBEffetIndesirableProduit2 } from 'src/app/utils/interfaces/new/bcb-tmp.interface';
import { Cim10 } from 'src/app/utils/interfaces/new/cim10.interface';
import { Filtres } from 'src/app/utils/interfaces/new/filtres.interface';
import { Paginated } from 'src/app/utils/interfaces/new/paginated.interface';
import { ProduitBcb } from 'src/app/utils/interfaces/new/produit-bcb.interface';
import { RechercheBcbProduit } from 'src/app/utils/interfaces/new/recherche-bcb-produit.interface';
import { VersionBcb } from 'src/app/utils/interfaces/new/version-bcb.interface';
import { environment } from 'src/environments/environment';
import { FiltresService } from '../filtres/filtres.service';
import { SnackbarService } from '../snackbar/snackbar.service';
import { BcbLpp, ProduitFromCodeLpp } from 'src/app/utils/interfaces/new/bcb-lpp.interface';
import { BCBSearchResult } from 'src/app/utils/interfaces/new/bcb-search-complete';

interface InfosProduitFlags {
  idProduit: number;
  deriveSang: boolean;
  anticoagulant: boolean;
  avk: boolean;
  vaccin: boolean;
  soluteDilution: boolean;
  contraceptionUrgence: boolean;
  contraceptifMineurs: boolean;
  frigo: boolean;
  stupefiant: boolean;
  antibioHumain: boolean;
  antibioVeterinaire: boolean;
  deconditionnement: boolean;
  vaccinGrippeSaisonniere: boolean;
  ghs: boolean;
  horsGhs: boolean;
  traitementSterilite: boolean;
  substancePsychotrope: boolean;
  reactifTest: boolean;
  laitPremierAge: boolean;
  medicamentIvg: boolean;
  medicamentMte: boolean;
  vaccinRor: boolean;
  dad: boolean;
  tvaCorse: boolean;
  mesusage: boolean;
  dprev: boolean;
  dau: boolean;
  rtu: boolean;
  dispCondTrodAngine: boolean;
  aod: boolean;
  traitementAsthme: boolean;
  anticancereuxOrauxLong: boolean;
  anticancereuxOrauxAutre: boolean;
  rva: boolean;
  medicamentOnereux: boolean;
  biosimilaire: boolean;
  referentBiosimilaire: boolean;
  substituableBiosimilaire: boolean;
  antimicrobienVeterinaire: boolean;
  vaccinCovid: boolean;
};

export interface InfosProduit {
  flags?: InfosProduitFlags;
  estRtu?: boolean;
  estRemboursable?: boolean;
  estAntibiotique?: boolean;
  codeMedicabase?: boolean;
  moleculeEprescription?: string;
}


@Injectable({
  providedIn: 'root'
})
export class BcbService {

  private httpOptions = {
    withCredentials: true,
    observe: 'response' as 'response',
    headers: new HttpHeaders(),
    params: undefined
  };

  private apiUri = environment.basePath;

  constructor(
    private _http: HttpClient,
    private _snack: SnackbarService,
    private _filtresService: FiltresService,
  ) { }

  public async getGuid(): Promise<string> {
    try {
      const res = await this._http.get(`${this.apiUri}/consultation/medicament/guid`, this.httpOptions).toPromise();
      const obj = res.body as any;
      return obj.guid;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async getVersion(): Promise<VersionBcb> {
    try {
      const res = await this._http.get(`${this.apiUri}/consultation/medicament/version`, this.httpOptions).toPromise();
      const obj = res.body as VersionBcb;
      return obj;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async getKey() {
    try {
      const res = await this._http.get(`${this.apiUri}/consultation/medicament/key-bcb`, this.httpOptions).toPromise();
      const key = res.body as any;
      return key;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async getTest(): Promise<any> {
    try {
      const res = await this._http.get(`${this.apiUri}/consultation/medicament/testBcb`, this.httpOptions).toPromise();
      const obj = res.body as any;
      return obj;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async isLicenceOk(): Promise<boolean> {
    try {
      const testLicence = await this.getTest();
      if (testLicence.statutConnexion === 1 || testLicence.statutConnexion === 0) {
        return true;
      } else {
        console.warn('Licence BCB non valide');
        console.warn(testLicence);
        return false;
      }
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public getBase64(guid: string, codeFonctionnalite: string, key: any) {
    try {
      const xml =
        `<?xml version="1.0" encoding="ISO-8859-1"?>
          <stream>
            <parametres>
              <fonctionnalite>${codeFonctionnalite}</fonctionnalite>
              <guid>${guid}</guid>
            </parametres>
            <autresparametres>
              <panier></panier>
              <tri></tri>
            </autresparametres>
            <key>
              <codeEditeur>HYPERMED</codeEditeur>
              <secretEditeur>${key}</secretEditeur>
            </key>
          </stream>`;

      return window.btoa(xml);
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public getBase64ATC(guid: string, key: any) {
    try {
      const xml =
        `<?xml version="1.0" encoding="ISO-8859-1"?>
          <stream>
          <parametres>
          <fonctionnalite>1</fonctionnalite>
          <guid>${guid}</guid>
          </parametres>
          <key>
          <codeEditeur>HYPERMED</codeEditeur>
          <secretEditeur>${key}</secretEditeur>
          <idPS></idPS>
          </key>
          </stream>`;

      return window.btoa(xml);
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public getBase64Therapeutic(guid: string, codeCIP: string, key) {
    try {
      const xml =
        `<?xml version="1.0" encoding="ISO-8859-1"?>
          <stream>
            <parametres>
              <fonctionnalite>6</fonctionnalite>
              <guid>${guid}</guid>
            </parametres>
          <ordonnance>
            <produit>
              <cip13>${codeCIP}</cip13>
            </produit>
          </ordonnance>
          <autresparametres>
            <panier></panier>
          </autresparametres>
          <key>
            <codeEditeur>HYPERMED</codeEditeur>
            <secretEditeur>${key}</secretEditeur>
            <idPS></idPS>
        </key>
          </stream>`;

      return window.btoa(xml);
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async getProduit(guid: string): Promise<ProduitBcb[]> {
    try {
      const res = await this._http.get(`${this.apiUri}/consultation/medicament/selection/${guid}`, this.httpOptions).toPromise();
      const produits = res.body as ProduitBcb[];
      return produits;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async searchProduitByNom(libelle: string): Promise<RechercheBcbProduit[]> {
    try {
      const res = await this._http.get(`${this.apiUri}/consultation/medicament/search/${libelle}`, this.httpOptions).toPromise();
      const produits = res.body as RechercheBcbProduit[];
      return produits;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }
  public async getMonographie (idProduit: string): Promise<string> {
    try {
      const res = await this._http.get(`${this.apiUri}/consultation/medicament/search/monographie/${idProduit}`, this.httpOptions).toPromise();
      const URLmono = res.body as string;
      return URLmono;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async getDocumentsProduit (idProduit: string): Promise<any> {
    try {
      const res = await this._http.get(`${this.apiUri}/consultation/medicament/search/documents/${idProduit}`, this.httpOptions).toPromise();
      const listDoc = res.body as string;
      return listDoc;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async searchBCBByNom(libelle: string,Type ='0',Location='0',Limite='100'): Promise<BCBSearchResult> {
    let params = new HttpParams()
    .set('Type', Type)
    .set('Location', Location)
    .set('Limite', Limite);
    // Clone the httpOptions object to avoid mutating the original one
    let httpOptionsWithParams = { ...this.httpOptions, params };

    try {
      const res = await this._http.get(`${this.apiUri}/consultation/medicament/search/complete/${libelle}`, httpOptionsWithParams).toPromise();
      const bcb_result = res.body as BCBSearchResult;
      return bcb_result;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  private _localProduits: Map<string, ProduitBcb[]> = new Map();
  public setLocalProduits(guid: string, produits: ProduitBcb[]) {
    this._localProduits.set(guid, produits);
  }
  public getLocalProduits(guid: string): ProduitBcb[] {
    return this._localProduits.get(guid);
  }

  public async getInfosProduit(idProduit: string): Promise<any & InfosProduit> {
    try {
      const res = await this._http.get(`${this.apiUri}/consultation/medicament/infos/${idProduit}`, this.httpOptions).toPromise();
      const produitInfos = res.body;

      if (((produitInfos as any)?.flags as InfosProduitFlags)?.rtu) {
        (produitInfos as InfosProduit).estRtu = true;
      } else {
        (produitInfos as InfosProduit).estRtu = false;
      }

      if ((produitInfos as any)?.baseRemboursementSS && Number((produitInfos as any)?.baseRemboursementSS) !== 0) {
        (produitInfos as InfosProduit).estRemboursable = true;
      } else {
        (produitInfos as InfosProduit).estRemboursable = false;
      }

      if (((produitInfos as any)?.flags as InfosProduitFlags)?.dispCondTrodAngine) {
        (produitInfos as InfosProduit).estAntibiotique = true;
      } else {
        (produitInfos as InfosProduit).estAntibiotique = false;
      }

      return produitInfos;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async getSamProduit(idProduit: string, cipProduit: string, idPatient: string): Promise<any> {
    try {
      // Si le produit ne contient pas de CIP alors on remplace cette partie du path par 'NO_CIP'
      const res = await this._http.get(`${this.apiUri}/consultation/medicament/sam/${idProduit}/${cipProduit && cipProduit !== '' ? cipProduit : 'NO_CIP'}/${idPatient}`, this.httpOptions).toPromise();
      const produitSam = res.body;
      return produitSam;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async getListPosologieProduit(idProduit: string, idPatient: string) {
    try {
      const res = await this._http.get(`${this.apiUri}/consultation/medicament/posologie/complete/${idProduit}/${idPatient}`, this.httpOptions).toPromise();
      const posologie = res.body;
      return posologie;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async getPosologieDetails(idProduit: string) {
    try {
      const res = await this._http.get(`${this.apiUri}/consultation/medicament/posologie/${idProduit}`, this.httpOptions).toPromise();
      const posologieDetails = res.body as any;
      return posologieDetails;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async getUnitesPriseProduit(idProduit: string) {
    try {
      const res = await this._http.get(`${this.apiUri}/consultation/medicament/posologie/produit/unite/${idProduit}`, this.httpOptions).toPromise();
      const unites = res.body;
      return unites;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async getCodesDurees() {
    try {
      const res = await this._http.get(`${this.apiUri}/consultation/medicament/posologie/codes/durees`, this.httpOptions).toPromise();
      const codes = res.body;
      return codes;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async getPosologieInformations() {
    try {
      const res = await this._http.get(`${this.apiUri}/consultation/medicament/posologie/informations/list`, this.httpOptions).toPromise();
      const codes = res.body;
      return codes;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async getCIM10Produit(idProduit: string) {
    try {
      const res = await this._http.get(`${this.apiUri}/consultation/medicament/cim10/${idProduit}`, this.httpOptions).toPromise();
      const cim10Produit = res.body;
      return cim10Produit;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async getCIM10(): Promise<Cim10[]> {
    try {
      const res = await this._http.get(`${this.apiUri}/consultation/medicament/codes/cim10`, this.httpOptions).toPromise();
      const codesCim10 = res.body as Cim10[];
      return codesCim10;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async searchCIM10(filtre: Filtres = {
    perpage: -1,
    currentPage: 1,
    sortOrder: SortEnum.ASC,
    sortBy: 'libelle'
  }): Promise<Paginated<Cim10>> {
    try {
      this.httpOptions.headers = this._filtresService.setHeaders(filtre);
      const res = await this._http.get(`${this.apiUri}/consultation/medicament/cim10/search`, this.httpOptions).toPromise();
      const codesCim10 = res.body as Cim10[];
      const paginated: Paginated<Cim10> = {
        page: Number(res.headers.get('x-page')),
        perpage: Number(res.headers.get('x-per-page')),
        total: Number(res.headers.get('x-total')),
        objects: codesCim10
      };
      return paginated;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async countNbreBoite(idProduit: string, bcbConditionnement: BcbConditionnement) {
    try {
      const res = await this._http.post(`${this.apiUri}/consultation/medicament/nombre-boites/${idProduit}`, bcbConditionnement, this.httpOptions).toPromise();
      const boites = res.body as any;
      return boites;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async getCoutPrescription(bcbPrescription: BcbPrescription) {
    try {
      const res = await this._http.post(`${this.apiUri}/consultation/medicament/cout/prescription`, bcbPrescription, this.httpOptions).toPromise();
      const cout = res.body as BcbPrescription;
      return cout;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async checkPoslogie(idPatient: string, bcbPosologie: BcbPosologieStructuree) {
    try {
      bcbPosologie.idProduit = String(bcbPosologie.idProduit);
      const res = await this._http.post(`${this.apiUri}/consultation/medicament/posologie/controle/${idPatient}`, bcbPosologie, this.httpOptions).toPromise();
      const posologie = res.body;
      return posologie;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async checkPoslogieList(idPatient: string, bcbPosologieList: BcbPosologieStructuree[]): Promise<BcbControlePosologieResult[]> {
    try {
      for (const poso of bcbPosologieList) {
        poso.idProduit = String(poso.idProduit);
      }
      const res = await this._http.post<BcbControlePosologieResult[]>(`${this.apiUri}/consultation/medicament/posologie/controleList/${idPatient}`, bcbPosologieList, this.httpOptions).toPromise();
      const controlePosos = res.body;
      return controlePosos;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async checkPoslogieList2(idPatient: string, bcbPosologieList2: BcbPosologieStructuree2[]): Promise<BcbControlePosologieResult2[]> {
      try {
        const res = await this._http.post<BcbControlePosologieResult[]>(`${this.apiUri}/consultation/medicament/posologie/controleList2/${idPatient}`, bcbPosologieList2, this.httpOptions).toPromise();
        const controlePosos = res.body;
        return controlePosos;
      } catch (error) {
        this._snack.openSnackBar(error.error.message);
      }
  }

  public async checkOrdonnance(idsProduit: Number[], idPatient: string) {
    try {
      const res = await this._http.post(`${this.apiUri}/consultation/medicament/ordonnance/controle/${idPatient}`, idsProduit, this.httpOptions).toPromise();
      const ordonnanceControle = res.body;
      return ordonnanceControle;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async PreCheckOrdonnance(idsProduit: Number[], idPatient: string) {
    try {
      const res = await this._http.post(`${this.apiUri}/consultation/medicament/ordonnance/precontrole/${idPatient}`, idsProduit, this.httpOptions).toPromise();
      const ordonnanceControle = res.body;
      return ordonnanceControle;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async getProduitFrequentEffetIndesirable(produitsId: Array<string>): Promise<Array<BCBEffetIndesirableProduit2>> {
    return new Promise<Array<BCBEffetIndesirableProduit2>>(async (resolve, reject) => {
      try {
        const options = {...this.httpOptions};
        options.params = {
          lstIdProduits: encodeURI(produitsId.join(',')).replace(/,/gm, '%2C')
          // lstIdProduits: produitsId[0]
        };

        const res = await this._http.get<Array<BCBEffetIndesirableProduit2>>(
          `${this.apiUri}/consultation/medicament/ordonnance/effetsIndesirables`,
          options
        ).toPromise();
        resolve(res.body);
      } catch (e) {
        reject(e);
      }
    });
  }

  public async rechercheLpp(libelle: string): Promise<Array<BcbLpp>> {
    return new Promise<Array<BcbLpp>>(async (resolve, reject) => {
      try {
        const res = await this._http.get<Array<BcbLpp>>(`${this.apiUri}/consultation/dispositif-medical/recherche-lpp/${libelle}`).toPromise();
        resolve(res);
      } catch (e) {
        reject(e);
      }
    });
  }

  public async rechercheProduitByCodeLpp(codeLpp: string): Promise<Array<ProduitFromCodeLpp>> {
    return new Promise<Array<ProduitFromCodeLpp>>(async (resolve, reject) => {
      try {
        const res = await this._http.get<Array<ProduitFromCodeLpp>>(`${this.apiUri}/consultation/dispositif-medical/recherche-produit-avec-code-lpp/${codeLpp}`).toPromise();
        resolve(res);
      } catch (e) {
        reject(e);
      }
    });
  }

  public async rechercheProduitByCodeCip(codeCip: string): Promise<RechercheBcbProduit[]> {
    return new Promise<RechercheBcbProduit[]>(async (resolve, reject) => {
      try {
        const res = await this._http.get<RechercheBcbProduit[]>(`${this.apiUri}/consultation/medicament/recherche-cip/${codeCip}`).toPromise();
        resolve(res);
      } catch (e) {
        reject(e);
      }
    });
  }

  public async rechercheProduitByCodeAtc(codeAtc: string): Promise<RechercheBcbProduit[]> {
    return new Promise<RechercheBcbProduit[]>(async (resolve, reject) => {
      try {
        const res = await this._http.get<RechercheBcbProduit[]>(`${this.apiUri}/consultation/medicament/recherche-atc/${codeAtc}`).toPromise();
        resolve(res);
      } catch (e) {
        reject(e);
      }
    });
  }
}
